/**
 * Auth Callback View
 *
 * Handles the OAuth callback from Azure AD.
 * Shows a loading state while checking authentication.
 * Redirects to dashboard on success, login on failure.
 */

import { createSpinner, renderIcon } from '../utils/icons';
import { handleAuthCallback } from '../services/auth-service';
import { getLinkRouter } from '../components/link';
import { restartSessionMonitoring } from '../services/session-manager';

/**
 * Create the auth callback view element
 */
export function createAuthCallbackView(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'min-h-screen flex items-center justify-center p-4';

  // Create glass card container
  const card = document.createElement('div');
  card.className = 'glass-card w-full max-w-md p-8 rounded-2xl text-center';

  // Status section (shows spinner or result)
  const statusSection = document.createElement('div');
  statusSection.id = 'callback-status';
  statusSection.className = 'space-y-4';

  // Loading state
  const loadingState = document.createElement('div');
  loadingState.id = 'callback-loading';
  loadingState.className = 'space-y-4';
  loadingState.innerHTML = `
    <div class="flex justify-center">
      ${createSpinner({ size: 'xl', className: 'text-primary' }).outerHTML}
    </div>
    <div>
      <h2 class="text-xl font-semibold text-foreground mb-2">Completing Sign In</h2>
      <p class="text-muted-foreground">Please wait while we verify your credentials...</p>
    </div>
  `;

  // Success state (hidden by default)
  const successState = document.createElement('div');
  successState.id = 'callback-success';
  successState.className = 'hidden space-y-4';
  successState.innerHTML = `
    <div class="flex justify-center">
      <div class="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center">
        ${renderIcon('checkCircle', { size: 'xl', className: 'text-success' }).outerHTML}
      </div>
    </div>
    <div>
      <h2 class="text-xl font-semibold text-foreground mb-2">Sign In Successful</h2>
      <p class="text-muted-foreground">Redirecting you to the dashboard...</p>
    </div>
  `;

  // Error state (hidden by default)
  const errorState = document.createElement('div');
  errorState.id = 'callback-error';
  errorState.className = 'hidden space-y-4';

  const errorIcon = document.createElement('div');
  errorIcon.className = 'flex justify-center';
  errorIcon.innerHTML = `
    <div class="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center">
      ${renderIcon('xCircle', { size: 'xl', className: 'text-destructive' }).outerHTML}
    </div>
  `;

  const errorContent = document.createElement('div');
  const errorTitle = document.createElement('h2');
  errorTitle.className = 'text-xl font-semibold text-foreground mb-2';
  errorTitle.textContent = 'Sign In Failed';

  const errorMessage = document.createElement('p');
  errorMessage.id = 'callback-error-message';
  errorMessage.className = 'text-muted-foreground mb-4';
  errorMessage.textContent = 'An error occurred during authentication.';

  const retryButton = document.createElement('button');
  retryButton.type = 'button';
  retryButton.className = `
    inline-flex items-center gap-2 px-4 py-2
    bg-primary text-primary-foreground font-medium
    rounded-lg transition-all duration-200
    hover:bg-primary/90
    focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background
  `.replace(/\s+/g, ' ').trim();
  retryButton.innerHTML = `
    ${renderIcon('logIn', { size: 'sm' }).outerHTML}
    <span>Try Again</span>
  `;
  retryButton.addEventListener('click', () => {
    const router = getLinkRouter();
    if (router) {
      router.navigate('/login');
    } else {
      window.location.href = '/login';
    }
  });

  errorContent.appendChild(errorTitle);
  errorContent.appendChild(errorMessage);
  errorContent.appendChild(retryButton);

  errorState.appendChild(errorIcon);
  errorState.appendChild(errorContent);

  // Assemble status section
  statusSection.appendChild(loadingState);
  statusSection.appendChild(successState);
  statusSection.appendChild(errorState);

  card.appendChild(statusSection);
  container.appendChild(card);

  // Process the callback
  processCallback(container);

  return container;
}

/**
 * Process the OAuth callback
 */
async function processCallback(container: HTMLElement): Promise<void> {
  const loadingState = container.querySelector('#callback-loading');
  const successState = container.querySelector('#callback-success');
  const errorState = container.querySelector('#callback-error');
  const errorMessage = container.querySelector('#callback-error-message');

  // Small delay to show loading state
  await new Promise(resolve => setTimeout(resolve, 500));

  try {
    const isAuthenticated = await handleAuthCallback();

    if (isAuthenticated) {
      // Show success state
      if (loadingState) loadingState.classList.add('hidden');
      if (successState) successState.classList.remove('hidden');

      // Initialize session manager for the new session
      restartSessionMonitoring();

      // Redirect to dashboard after brief delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      const router = getLinkRouter();
      if (router) {
        router.navigate('/dashboard', true);
      } else {
        window.location.href = '/dashboard';
      }
    } else {
      // Show error state
      if (loadingState) loadingState.classList.add('hidden');
      if (errorState) errorState.classList.remove('hidden');
      if (errorMessage) {
        errorMessage.textContent = 'Authentication could not be verified. Please try signing in again.';
      }
    }
  } catch (error) {
    console.error('Auth callback error:', error);

    // Show error state
    if (loadingState) loadingState.classList.add('hidden');
    if (errorState) errorState.classList.remove('hidden');
    if (errorMessage) {
      errorMessage.textContent = error instanceof Error ? error.message : 'An unexpected error occurred.';
    }
  }
}

/**
 * Auth callback view renderer for router
 */
export function authCallbackView(): HTMLElement {
  return createAuthCallbackView();
}

export default authCallbackView;
